/**
 * Device Registration Wizard — thin orchestrator.
 * Step bodies live in /wizard/device/, shared shell in /wizard/WizardShell.jsx.
 */
import { useEffect, useState } from 'react';
import { controlApi } from '@/services/api';
import { toast } from 'sonner';
import {
  Cpu, Wifi, Building2, CheckCircle,
} from 'lucide-react';
import { WizardShell } from './wizard/WizardShell';
import { DeviceStep1Info } from './wizard/device/DeviceStep1Info';
import { DeviceStep2Capabilities } from './wizard/device/DeviceStep2Capabilities';
import { DeviceStep3Assignment } from './wizard/device/DeviceStep3Assignment';
import { DeviceStep4Review } from './wizard/device/DeviceStep4Review';

const STEPS = [
  { id: 1, title: 'Device Info', icon: Cpu, description: 'Basic device information' },
  { id: 2, title: 'Capabilities', icon: Wifi, description: 'Hardware features' },
  { id: 3, title: 'Assignment', icon: Building2, description: 'Customer & location' },
  { id: 4, title: 'Review', icon: CheckCircle, description: 'Confirm & register' },
];

const BLANK = {
  serial_number: '',
  module_sn: '',
  device_model: 'TRK-100',
  firmware_version: '1.0.0',
  has_gps: true,
  has_temperature: true,
  has_humidity: false,
  has_weight: false,
  has_bag_sensor: false,
  tenant_id: '',
  location: '',
  notes: '',
};

const STEP_VALIDATORS = {
  1: (data) => (data.serial_number.trim() ? null : 'Serial number is required'),
  2: (data) => (data.has_gps || data.has_temperature
    ? null
    : 'Device must have at least GPS or Temperature capability'),
  3: (data) => (data.tenant_id ? null : 'Please assign the device to a customer'),
};

export default function DeviceRegistrationWizard({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [formData, setFormData] = useState(BLANK);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await controlApi.getTenants();
        if (!cancelled) setTenants(res.data || []);
      } catch (error) {
        console.error('Failed to load tenants for device wizard:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    const validator = STEP_VALIDATORS[step];
    const err = validator ? validator(formData) : null;
    if (err) {
      toast.error(err);
      return;
    }
    setStep((prev) => Math.min(prev + 1, STEPS.length));
  };

  const handleBack = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleReset = () => {
    setStep(1);
    setFormData(BLANK);
    onClose?.();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await controlApi.createDevice({
        serial_number: formData.serial_number,
        module_sn: formData.module_sn || null,
        device_model: formData.device_model,
        firmware_version: formData.firmware_version,
        capabilities: {
          gps: formData.has_gps,
          temperature: formData.has_temperature,
          humidity: formData.has_humidity,
          weight: formData.has_weight,
          bag_sensor: formData.has_bag_sensor,
        },
        tenant_id: formData.tenant_id || null,
        location: formData.location || null,
        notes: formData.notes || null,
        status: 'active',
      });
      toast.success('Device registered successfully!');
      onSuccess?.();
      handleReset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register device');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <WizardShell
      title="Register New Device"
      steps={STEPS}
      step={step}
      loading={loading}
      onClose={handleReset}
      onBack={handleBack}
      onNext={handleNext}
      onSubmit={handleSubmit}
      submitLabel="Register Device"
      submittingLabel="Registering..."
      backTestId="wizard-back-btn"
      nextTestId="wizard-next-btn"
      submitTestId="wizard-submit-btn"
    >
      {step === 1 && <DeviceStep1Info formData={formData} onChange={handleChange} />}
      {step === 2 && <DeviceStep2Capabilities formData={formData} onChange={handleChange} />}
      {step === 3 && <DeviceStep3Assignment formData={formData} onChange={handleChange} tenants={tenants} />}
      {step === 4 && <DeviceStep4Review formData={formData} tenants={tenants} />}
    </WizardShell>
  );
}
